import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import type { InspectorTarget } from '../components/AnalyticsInspector';
import { useAnalyticsFilters } from './useAnalyticsFilters';

interface InspectorContextValue {
  target: InspectorTarget | null;
  open: (target: InspectorTarget) => void;
  close: () => void;
}

const InspectorContext = createContext<InspectorContextValue | null>(null);

export function InspectorProvider({ children }: { children: React.ReactNode }) {
  const { filters } = useAnalyticsFilters();
  const [target, setTarget] = useState<InspectorTarget | null>(null);

  const open = useCallback((t: InspectorTarget) => {
    setTarget(t);
    trackAnalyticsEvent('admin_analytics_inspector_opened', {
      target_kind: t.kind,
      source_view: filters.view,
      timestamp: new Date().toISOString(),
    });
  }, [filters.view]);
  const close = useCallback(() => setTarget(null), []);

  const value = useMemo(() => ({ target, open, close }), [target, open, close]);

  return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}

export function useInspector(): InspectorContextValue {
  const ctx = useContext(InspectorContext);
  if (!ctx) throw new Error('useInspector must be used within an InspectorProvider');
  return ctx;
}
